import { max, min_value } from '@vee-validate/rules';
import { defineRule } from 'vee-validate';
import {pDecimalValidationTest} from '@/components/mixins/ValidatedInput.js';
import SharedValidation from './SharedValidation.js';
import {dateFormatter} from "@/components/Common/Formatters.js";


defineRule('unique-value', (value, { isDuplicated }) => {
    if (!isDuplicated(value)) return true;
    return `Value cannot be be duplicated.`;
});

defineRule('valid-isbn', (value, { isValidISBN }) => {
    if (isValidISBN(value)) return true;
    return `The ISBN length excluding dashes must be 10 or 13.`;
});

defineRule('required-if-used-dcc', (value, { hasUsedDcc, rowData }) => {
    if (!hasUsedDcc(rowData)) {
        return true;
    }
    if (value !== null && value !== undefined && value !== '') return true;
    return `This field is required when a Used DCC is provided.`;
});

export default {
    mixins: [SharedValidation],
    data() {
        return {
            bookConfiguration: {
                type: 'books',
                label: 'Text Books',
                canCreateUsed: true,
                sampleNotes: [
                    'Any Code fields left blank will be generated.',
                    'Barcodes will be generated from ISBN.',
                    'Binding matches on abbreviation.',
                    'DCC fields are a concatenation of department, class, and category 2 digit numbers (010530).',
                    'Leave UsedDCC empty to only create new books, no used books will be created.',
                    'Dates are expected to be in YYYY-MM-DD format.'
                ],
                fields: [
                    {
                        key: 'SupplierCode',
                        label: 'Supplier Code',
                        dataType: 'suppliers',
                        comparisonProperty: 'code',
                        hasErrorCalculator: value => {
                            return (
                                value === '' ||
                                value === undefined ||
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        rules: 'min_value:0|required',
                        samples: ['AMY001','BAR001','YAR001']
                    },
                    {
                        key: 'NewDCC',
                        label: 'New DCC',
                        selectOptionsFilter: () => {return 2;},
                        hasErrorCalculator: (value) => {
                            return !this.matchesExistingObject(value, true);
                        },
                        rules: 'min_value:0|required',
                        samples: ['101010','101010','101010'],
                        comparisonProperty: 'dccNumbers',
                        dataType: 'dcc',
                    },
                    {
                        key: 'UsedDCC',
                        label: 'Used DCC',
                        selectOptionsFilter: () => {return 2;},
                        hasErrorCalculator: (value) => {
                            // Used DCCs are ignored by the import when used books aren't being created.
                            if (!this.createUsed) {
                                return false;
                            }

                            return !this.matchesExistingObject(value);
                        },
                        hasWarningCalculator: (value) => {
                            // Both cases mean the row's used book won't be created.
                            if (this.createUsed) {
                                return this.isEmpty(value);
                            }

                            return !this.isEmpty(value);
                        },
                        warningMessage: () => {
                            if (this.createUsed) {
                                return 'No Used DCC provided, a used book will not be created.';
                            }

                            return 'Create used books is not checked, a used book will not be created.';
                        },
                        rules: 'min_value:0',
                        samples: ['101010','101010','101010'],
                        comparisonProperty: 'dccNumbers',
                        dataType: 'dcc',
                        convertToNull: true
                    },
                    {
                        key: 'ISBN',
                        label: 'ISBN',
                        hasErrorCalculator: (value, row, checkOriginalData) => {                        
                            if (this.isEmpty(value)) {
                                return false;
                            }

                            return value.length < 10 || 
                                this.isDuplicatedOnImportISBN(value, checkOriginalData) || 
                                !this.isValidISBN(value)
                        },
                        rules: {
                            'unique-value': {
                                isDuplicated: this.isDuplicatedOnImportISBN
                            },
                            'valid-isbn': {
                                isValidISBN: this.isValidISBN
                            },                            
                            min: {length: 10},
                        },
                        samples: ['978-0-534-58484-9','978-0-534-58486-3','978-0-534-58505-1'],
                        convertToNumber: false
                    },
                    {
                        key: 'StatusDate',
                        label: 'Status Date',
                        hasWarningCalculator: value => {
                            return !this.isValidDate(value);
                        },
                        warningDefault: null,
                        rules: {
                            'valid-date': {
                                isValidDate: this.isValidDate,
                                propertyName: 'StatusDate'
                            }
                        },
                        inputType: 'date',
                        formatter: dateFormatter,
                        addRowDataToRule: 'valid-date',
                        samples: [
                            '"2022-10-09"',
                            '"2022-10-09"',
                            '"2022-10-05"'
                        ]
                    },
                    {
                        key: 'Edition',
                        label: 'Edition',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: null,
                        rules: {
                            max: {length: 20}
                        },
                        samples: ['"1"','"2"','"1"'],
                        convertToNumber: false
                    },
                    {
                        key: 'Copyright',
                        label: 'Copyright',
                        hasWarningCalculator: value => {
                            return max(value, { length: 10 }) !== true;
                        },
                        warningDefault: null,
                        rules: {
                            max: {length: 10}
                        },
                        samples: ['"14"','"18"','"22"'],
                        convertToNumber: false
                    },
                    {
                        key: 'Binding',
                        label: 'Binding',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['"3R"', '"AC"', 'NO'],
                        dataType: 'bindings',
                        comparisonProperty: 'abbreviation'
                    },
                    {
                        key: 'Imprint',
                        label: 'Imprint',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: null,
                        rules: {
                            max: {length: 20}
                        },
                        samples: ['"Avery"','"DK"','"DK"'],
                        convertToNumber: false
                    },
                    {
                        key: 'Status',
                        label: 'Status',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['"None"', '"New Edition Pre"', '"Old Edition"'],
                        dataType: 'textStatus'
                    },
                    {
                        key: 'NewCost',
                        label: 'New Cost',
                        hasErrorCalculator: value => {
                            return (
                                value &&
                                !pDecimalValidationTest(value, {
                                    precision: '10',
                                    scale: '2'
                                })
                            );
                        },
                        rules: 'p-decimal:10,2|min_value:0',
                        samples: [10.55, 50, 50],
                        convertToNumber: true
                    },
                    {
                        key: 'UsedCost',
                        label: 'Used Cost',
                        hasWarningCalculator: value => {
                            return (
                                value &&
                                !pDecimalValidationTest(value, {
                                    precision: '10',
                                    scale: '2'
                                })
                            );
                        },
                        warningDefault: null,
                        rules: 'p-decimal:10,2|min_value:0',
                        samples: [10.55, 50, 50],
                        convertToNumber: true
                    },
                    {
                        key: 'NewRetail',
                        label: 'New Retail',
                        hasErrorCalculator: value => {
                            return (
                                !pDecimalValidationTest(value, {
                                    precision: '10',
                                    scale: '2'
                                })
                            );
                        },
                        rules: 'p-decimal:10,2|min_value:0|required',
                        samples: [10.55, 50, 50],
                        convertToNumber: true
                    },
                    {
                        key: 'UsedRetail',
                        label: 'Used Retail',
                        hasErrorCalculator: (value, row) => {
                            if (this.isEmpty(value)) {
                                return this.hasUsedDcc(row);
                            }

                            return !pDecimalValidationTest(value, {
                                precision: '10',
                                scale: '2'
                            });
                        },
                        rules: {
                            'required-if-used-dcc': {
                                hasUsedDcc: this.hasUsedDcc
                            },
                            'p-decimal': { precision: 10, scale: 2 },
                            min_value: { min: 0 }
                        },
                        addRowDataToRule: 'required-if-used-dcc',
                        samples: [10.55, 50, 50],
                        convertToNumber: true
                    },
                    {
                        key: 'Title',
                        label: 'Title',
                        canConvertCase: true,
                        hasErrorCalculator: value => {
                            return this.isEmpty(value) || max(value, { length: 200 }) !== true;
                        },
                        rules: {
                            max: {length: 200},
                            required: true
                        },
                        samples: ['"My Book Title1"','"My Book Title2"','"My Book Title3"']
                    },
                    {
                        key: 'Author',
                        label: 'Author',
                        canConvertCase: true,
                        hasErrorCalculator: value => {
                            return this.isEmpty(value) || max(value, { length: 200 }) !== true;
                        },
                        rules: {
                            max: { length: 200 },
                            required: true
                        },
                        samples: ['"Doe, Jane"','"Doe, John"','"Doe, Jane"']
                    },
                    {
                        key: 'NewBuyBackTitle',
                        label: 'New Buy Back Title',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['Yes', 'No', 'Yes'],
                        dataType: 'yesNoOptions',
                        defaultValue: 'No'
                    },
                    {
                        key: 'UsedBuyBackTitle',
                        label: 'Used Buy Back Title',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['Yes', 'No', 'Yes'],
                        dataType: 'yesNoOptions',
                        defaultValue: 'No'
                    },
                    {
                        key: 'NewWantListTitle',
                        label: 'New Want List Title',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['Yes', 'No', 'Yes'],
                        dataType: 'yesNoOptions',
                        defaultValue: 'No'
                    },
                    {
                        key: 'UsedWantListTitle',
                        label: 'Used Want List Title',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['Yes', 'No', 'Yes'],
                        dataType: 'yesNoOptions',
                        defaultValue: 'No'
                    },
                    {
                        key: 'NewDiscontinued',
                        label: 'New Discontinued',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['Yes', 'No', 'Yes'],
                        dataType: 'yesNoOptions',
                        defaultValue: 'No'
                    },
                    {
                        key: 'UsedDiscontinued',
                        label: 'Used Discontinued',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['Yes', 'No', 'Yes'],
                        dataType: 'yesNoOptions',
                        defaultValue: 'No'

                    },
                    {
                        key: 'Comment',
                        label: 'Comment',
                        hasWarningCalculator: value => {
                            return max(value, { length: 200 }) !== true;
                        },
                        warningDefault: null,
                        rules: {
                            max: {length: 200}
                        },
                        samples: ['"Product Comment1"','"Product Comment2"','""']
                    }
                ]
            }
        };
    },
    methods: {
        hasUsedDcc(row) {
            return this.createUsed && !this.isEmpty(row?.UsedDCCId);
        },
        isDuplicatedOnImportISBN(isbn, checkOriginalData) {
            return (
                isbn != '' &&
                this.loadedData.filter(x => x.ISBN === isbn||
                    (checkOriginalData &&
                        x.originalData?.ISBN === isbn)).length > 1
            );
        },
        isValidISBN(isbn){
            var isbnCompressed = isbn.replace(/-/g,'');
            return (isbnCompressed.length === 10 || isbnCompressed.length === 13);
        }
    }
};
